'use client';

import { useEffect, useState } from 'react';

import { useRouter } from 'next/navigation';

import { Button, Container } from '@/components/ui';
import type { z } from '@/libs';
import { ROUTES } from '@/types/enums';
import { attemptChunkRecovery, getChunkErrorConfidence } from '@/utils/chunkError';
import { logger } from '@/utils/logger';

const ErrorPage = ({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) => {
  const router = useRouter();
  const chunkErrorConfidence = getChunkErrorConfidence(error);
  const chunkError = chunkErrorConfidence !== null;
  const [isRecovering, setIsRecovering] = useState(chunkError);

  useEffect(() => {
    logger.error('[app/error] uncaught error', {
      channel: 'error-boundary',
      boundary: 'app/error',
      message: error?.message,
      digest: error?.digest,
      stack: error?.stack,
      isChunkError: chunkError,
      chunkErrorConfidence,
      url: globalThis.location?.href,
    });

    if (chunkError) {
      setIsRecovering(attemptChunkRecovery('app/error'));
    }
  }, [chunkError, chunkErrorConfidence, error]);

  const handleGoHome = () => {
    const home: z.infer<typeof ROUTES> = ROUTES.enum['/'];
    router.push(home);
  };

  const handleRetry = () => {
    if (chunkError) {
      globalThis.location.reload();
      return;
    }
    reset();
  };

  if (isRecovering) return null;

  const description = {
    high: 'O site foi atualizado enquanto você navegava. Recarregue a página para continuar.',
    low: 'Não foi possível carregar um recurso da página. Recarregue para tentar novamente.',
    none: 'Algo deu errado. Nossa equipe foi notificada e está trabalhando para resolver o problema.',
  }[chunkErrorConfidence ?? 'none'];

  return (
    <Container className="flex min-h-screen items-center justify-center py-4 md:py-0">
      <div className="flex-1 md:flex-none flex flex-col items-center md:min-h-[50vh]">
        <div className="flex-1 flex flex-col items-center justify-center gap-2">
          <h1 className="text-6xl md:text-8xl font-extrabold text-primary">500</h1>
          <h2 className="text-base md:text-xl font-semibold">
            {chunkError
              ? 'Não foi possível carregar a página'
              : 'Erro interno do servidor'}
          </h2>
          <p className="text-sm md:text-base text-center">{description}</p>
        </div>

        <div className="flex-1 flex flex-col-reverse md:flex-row md:justify-center w-full gap-2">
          <Button
            variant="ghost"
            size="large"
            onClick={handleRetry}
            className="!bg-foreground/10 !hover:!bg-foreground/20"
          >
            {chunkError ? 'Recarregar página' : 'Tentar novamente'}
          </Button>

          <Button variant="secondary" size="large" onClick={handleGoHome}>
            Voltar ao início
          </Button>
        </div>
      </div>
    </Container>
  );
};

export default ErrorPage;
